import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideTicketDetailsPopup, setTickets, setCompanyTickets } from "../../Pages/Store";
import "bootstrap/dist/css/bootstrap.min.css";
import { toast } from "react-toastify";
import { RiDeleteBin2Line } from "react-icons/ri";
import { IoIosArrowUp, IoIosArrowDown } from "react-icons/io";
import { getTicket, updateTicket, getAllTickets, getCompanyTickets, getErrorMessage } from "../../services/api";

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = (error) => reject(error);
  });
};

const getCreds = () =>
  JSON.parse(localStorage.getItem("ssCreds") || "{}");

const downloadAttachment = (attachment) => {
  try {
    const byteCharacters = atob(attachment.Content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: attachment.ContentType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = attachment.Filename || "attachment";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    toast.error("Failed to download attachment");
  }
};

const TicketDetails = () => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const containerRef = useRef(null);
  const { ticketDetails, ticketSource } = useSelector((state) => state.root);
  const [fullTicket, setFullTicket] = useState(null);
  const details = fullTicket || ticketDetails;
  const [commentsTable, setCommentsTable] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [commentsNotesTable, setCommentsNotesTable] = useState([]);

  const loadDetails = async () => {
    try {
      const { username, password } = getCreds();
      const response = await getTicket(username, password, ticketDetails?.id);
      setFullTicket(response.data);
      setCommentsNotesTable(response.data.comments || []);
    } catch (error) {
      toast.error(
        getErrorMessage(error, "Failed to load ticket details"),
        { position: "top-right", autoClose: 3000 }
      );
    }
  };

  useEffect(() => {
    if (ticketDetails?.id) {
      loadDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketDetails?.id]);

  const handleClose = () => {
    dispatch(hideTicketDetailsPopup());
  };

  const refreshTicketList = async (username, password) => {
    if (ticketSource === "company") {
      const response = await getCompanyTickets(username, password);
      dispatch(setCompanyTickets(response.data.tickets));
    } else {
      const response = await getAllTickets(username, password);
      dispatch(setTickets(response.data.tickets));
    }
  };

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);

    const maxSizeMB = 5; // Max size per file

    // Allowed file extensions
    const allowedExtensions = new Set([
     'png', 'jpg', 'jpeg', 'gif', 'tiff', 'pdf',
    'xls', 'xlsx', 'doc', 'docx', 'ppt', 'pptx',
    'txt', 'html', 'json', 'csv'])

    // Convert MB to bytes
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (selectedFiles.length > 1) {
      setFormErrors({ ...formErrors, file: "Only one attachment is allowed" });
      fileInputRef.current.value = null; // Clear input field
      return;
    }

    const file = selectedFiles[0];
    if (!file) return;

    const fileExtension = file.name.split('.').pop().toLowerCase();

    if (!allowedExtensions.has(fileExtension)) {
      setFormErrors({ ...formErrors, file: "Unsupported file format" });
      fileInputRef.current.value = null; // Clear input field
      return;
    }

    if (file.size > maxSizeBytes) {
      setFormErrors({ ...formErrors, file: `Maximum upload file size: ${maxSizeMB} MB` });
      fileInputRef.current.value = null; // Clear input field
      return;
    }

    setFiles([file]); // Replace any previously selected file — only one allowed
    setFormErrors((prevErrors) => {
      const { file, ...rest } = prevErrors;
      return rest;
    });
  };

  const handleFileDelete = (fileToDelete) => {
    setFiles(files.filter((file) => file !== fileToDelete));
    // Clear the file input field when file is deleted
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const clearInputs = () => {
    setTitle("");
    setDescription("");
    setFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const handleComments = () => {
    setFormErrors({}); // Clear any previous errors

    setCommentsTable(!commentsTable);
    if (!commentsTable && containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const UpdateStatus = async (statusApi) => {
    try {
      const { username, password } = getCreds();
      const label = statusApi === "Closed" ? "closed" : "reopened";

      await updateTicket(username, password, details?.id, {
        Ticket: {
          State: statusApi,
          TicketID: details?.id,
        },
        Article: {
          Subject: `Ticket ${label} via Self Service`,
          Body: `This ticket has been ${label} via the Self Service Portal.`,
          ContentType: "text/plain; charset=utf-8",
        },
      });

      if (statusApi === "Closed") {
        toast.success("Ticket has been Closed", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        toast.success("Ticket has been Reopened", {
          position: "top-right",
          autoClose: 3000,
        });
      }

      await refreshTicketList(username, password);
      handleClose();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed"), {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const attachments = details?.attachments || [];

  // Split text into chunks of `length` chars (line breaks start a new chunk),
  // without constructing a RegExp from variables (ReDoS-safe)
  const splitTextByLength = (text, length) => {
    if (!text) return text;
    const chunks = [];
    String(text)
      .split("\n")
      .forEach((line) => {
        for (let i = 0; i < line.length; i += length) {
          chunks.push(line.slice(i, i + length));
        }
      });
    return chunks;
  };

  const validateForm = () => {
    const errors = {};
    const trimmedTitle = title?.trim();
    if (!trimmedTitle) {
      errors.title = "Subject required";
    } else if (trimmedTitle.length > 200) {
      errors.title = "Subject cannot exceed 200 characters";
    }
    if (!description?.trim()) errors.description = "Comments required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const sendComments = async () => {
    if (!validateForm()) return;

    try {
      const { username, password } = getCreds();

      const updateData = {
        Ticket: {
          State: details?.status,
        },
        Article: {
          Subject: title,
          Body: description,
          ContentType: "text/plain; charset=utf-8",
        },
      };

      if (files.length > 0) {
        updateData.Article.Attachment = await Promise.all(
          files.map(async (file) => ({
            Content: await fileToBase64(file),
            ContentType: file.type || "application/octet-stream",
            Filename: file.name,
          }))
        );
      }

      await updateTicket(username, password, details?.id, updateData);

      // Clear the form as soon as the comment is saved, so a later
      // refresh failure can't leave stale text in the inputs.
      clearInputs();

      toast.success("Comments added successfully", {
        position: "top-right",
        autoClose: 3000,
      });

      try {
        await loadDetails();
        await refreshTicketList(username, password);
      } catch (refreshError) {
        console.error("Failed to refresh ticket data after comment", refreshError);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to add comment"), {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  return (
    <div id="addTicketPopup">
      <div
        className="card popup ticket-details-popup"
        ref={containerRef}
      >
        <div className="details-popup">
          <div className="details-header">
            <h5>Ticket Details</h5>
            <span
              className="closeDetails"
              onClick={handleClose}
              title="Close Popup"
            >
              &times;
            </span>
          </div>
          <div
            className="modal-body"
            style={{ padding: "1.25rem 1.5rem", maxHeight: "23rem", overflow: "auto" }}
          >
            <div className="row">
              <div className="col-md-6">
                <p>
                  <strong style={{ marginRight: "4px" }}>Case Number:</strong>{" "}
                  <span id="popupTicketNumber">
                    {details?.caseNumber || "-"}
                  </span>
                </p>
                <p>
                  <strong style={{ marginRight: "4px" }}>Case Title:</strong>{" "}
                  <span id="popupTicketName">{details?.title || "-"}</span>
                </p>
                <p>
                  <strong style={{ marginRight: "4px" }}>Created on:</strong>{" "}
                  <span id="popupCreated">
                    {details?.createdOn
                      ? details.createdOn.split(/[ T]/)[0]
                      : "-"}
                  </span>
                </p>
                <p>
                  <strong style={{ marginRight: "4px" }}>Created By:</strong>{" "}
                  <span id="popupCreatedName">
                    {details?.createdBy || "-"}
                  </span>
                </p>
              </div>
              <div className="col-md-6">
                <p>
                  <strong style={{ marginRight: "4px" }}>Status:</strong>{" "}
                  <span id="popupStatus">
                    {details?.status || "-"}
                  </span>
                </p>
                <p>
                  <strong style={{ marginRight: "4px" }}>Severity:</strong>{" "}
                  <span id="popupSeverity">
                    {details?.severity || "-"}
                  </span>
                </p>
                <p>
                  <strong style={{ marginRight: "4px" }}>Assign To:</strong>{" "}
                  <span id="popupSeverity">
                    {details?.assignedTo || "-"}
                  </span>
                </p>

                <p>
                  <strong style={{ marginRight: "8px" }}>
                    SLA Expires on:
                  </strong>{" "}
                  <span id="slaTime">
                    {details?.SLA || "-"}
                  </span>
                </p>
              </div>
              <div className="col-md-12">
                <p>
                  <strong style={{ marginRight: "4px" }}>Description:</strong>{" "}
                  <span id="popupDescription">
                    {details?.description || "-"}
                  </span>
                </p>
                {/* mo */}
                <p>
                  <strong style={{ marginRight: "4px" }}>Attachments:</strong>
                  {attachments?.filter((file) => file?.Filename).length > 0 ? (
                    <ol style={{ paddingLeft: "20px" }}>
                      {attachments
                        .filter((file) => file?.Filename)
                        .map((file, index) => (
                          <li key={index}>
                            <a
                              onClick={() => downloadAttachment(file)}
                              style={{ color: "black", cursor: "pointer" }}
                            >
                              {file?.Filename || "Document"}
                            </a>
                          </li>
                        ))}
                    </ol>
                  ) : (
                    "-"
                  )}
                </p>
              </div>
            </div>

            {commentsTable && (
              <>
                {/* Comments Table */}
                <div id="commentsTableContainer" style={{ marginTop: "2%" }}>
                  <table className="table table-bordered">
                    <thead style={{ textAlign: "center", textWrap: "nowrap" }}>
                      <tr>
                        <th scope="col" style={{ width: "7%" }}>
                          SL NO
                        </th>
                        <th scope="col" style={{ width: "15%" }}>
                          Date
                        </th>
                        <th scope="col" style={{ whiteSpace: "nowrap" }}>
                          Subject
                        </th>
                        <th scope="col">Comments</th>
                        <th scope="col" style={{ width: "13%" }}>
                          Documents
                        </th>
                      </tr>
                    </thead>
                    <tbody id="commentsTableBody">
                      {commentsNotesTable?.length > 0 ? (
                        <>
                          {commentsNotesTable?.map((comment, index) => (
                            <tr key={comment.id || index}>
                              <td
                                id="table-td-data"
                                style={{ textAlign: "center" }}
                              >
                                {index + 1}
                              </td>
                              <td
                                id="table-td-data"
                                style={{
                                  textAlign: "center",
                                  textWrap: "nowrap",
                                }}
                              >
                                {(comment.CreateTime || comment.date_entered)?.split(/[ T]/)[0]}
                              </td>
                              <td
                                id="table-td-data"
                                style={{ textAlign: "left" }}
                              >
                                {splitTextByLength(comment?.Subject, 100)?.map(
                                  (line, idx) => (
                                    <div key={idx}>{line}</div>
                                  )
                                )}
                              </td>
                              <td
                                id="table-td-data"
                                style={{ textAlign: "left" }}
                              >
                                {splitTextByLength(
                                  comment?.Body,
                                  200
                                )?.map((line, idx) => (
                                  <div key={idx}>{line}</div>
                                ))}
                              </td>
                              <td
                                id="table-td-data"
                                style={{ textAlign: "center" }}
                              >
                                {comment.Attachment?.length > 0 ? (
                                  <a
                                    onClick={() =>
                                      downloadAttachment(comment.Attachment[0])
                                    }
                                    style={{ cursor: "pointer" }}
                                  >
                                    {comment.Attachment[0]?.Filename}
                                  </a>
                                ) : (
                                  "-"
                                )}
                              </td>
                            </tr>
                          ))}
                        </>
                      ) : (
                        <tr>
                          <td colSpan={6}>No Comments Added</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {details.status !== "Closed" &&
                  details.status !== "Resolved" && (
                    <div>
                      <div className="form-group mb-3">
                        <label htmlFor="ticketTitle">
                          <b>
                            Subject<span className="required">*</span>
                          </b>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Title"
                          id="ticketTitle"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                        />
                        {formErrors.title && (
                          <span style={{ color: "#FF0000" }}>
                            {formErrors.title}
                          </span>
                        )}
                      </div>
                      <div className="form-group mb-3">
                        <label htmlFor="ticketDescription">
                          <b>
                            Comments<span className="required">*</span>
                          </b>
                        </label>
                        <textarea
                          className="form-control"
                          id="ticketDescription"
                          rows="3"
                          placeholder="Description"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                        ></textarea>
                        {formErrors.description && (
                          <span style={{ color: "#FF0000" }}>
                            {formErrors.description}
                          </span>
                        )}
                      </div>
                      <div className="form-group mb-3">
                        <label htmlFor="fileUpload">
                          <b>Add File (Optional)</b>
                        </label>
                        <input
                          type="file"
                          className="form-control-file"
                          id="fileUpload"
                          ref={fileInputRef}
                          onChange={handleFileChange}
                          data-toggle="tooltip"
                          data-placement="top"
                          title="
File Formats supported: png, jpg, jpeg, gif, tiff, pdf, xls, xlsx, doc, docx, ppt, pptx, txt, html, json, csv"
                          style={{
                            border: "1px solid #80808069",
                            borderRadius: "3px",
                            padding: "3px",
                          }}
                        />
                        {formErrors.file && (
                          <div className="error" style={{ color: "#FF0000" }}>
                            {/* {formErrors.file} */}
                          </div>
                        )}
                        {files.length > 0 && (
                          <>
                            {files.map((file, index) => (
                              <div key={index}>
                                {file.name}
                                <span
                                  onClick={() => handleFileDelete(file)}
                                  style={{
                                    marginLeft: "10px",
                                    color: "#ff0000",
                                    cursor: "pointer",
                                  }}
                                >
                                  <RiDeleteBin2Line title="Remove file" />
                                </span>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                      <button
                        className="btn btn-ss-primary"
                        onClick={sendComments}
                      >
                        Send
                      </button>
                    </div>
                  )}
              </>
            )}

            <div className="Button-grps" style={{ padding: "5px" }}>
              {details.status === "Resolved" && (
                <>
                  <button
                    className="btn btn-light add-btn"
                    title="Reopen Ticket"
                    onClick={() => {
                      UpdateStatus("Open");
                    }}
                  >
                    Reopen
                  </button>
                  <button
                    className="btn btn-light add-btn"
                    title="Close Ticket"
                    onClick={() => {
                      UpdateStatus("Closed");
                    }}
                  >
                    Close
                  </button>
                </>
              )}
              <button
                className="btn btn-light add-btn"
                onClick={handleComments}
                title={commentsTable ? "Hide Comments" : "Show Comments"}
              >
                {commentsTable ? (
                  <>
                    <IoIosArrowUp /> Comments
                  </>
                ) : (
                  <>
                    <IoIosArrowDown /> Comments
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDetails;
